import React, { useState } from 'react';
import { Collapse, Typography } from 'antd';
import { PlusOutlined, MinusOutlined } from '@ant-design/icons';
import { generateFAQSchema } from '../utils/schemaMarkup';
import { Helmet } from 'react-helmet';

const { Panel } = Collapse;
const { Title } = Typography;

const FAQSection = ({ 
  faqs, 
  title = "Frequently Asked Questions",
  style = {},
  collapsible = true 
}) => {
  const [activeKeys, setActiveKeys] = useState([]);

  // Generate FAQ schema for rich snippets
  const faqSchema = generateFAQSchema(faqs);

  const handleChange = (keys) => {
    setActiveKeys(keys);
  };

  const customExpandIcon = ({ isActive }) => (
    <span style={{ color: '#667eea', fontWeight: 'bold' }}>
      {isActive ? <MinusOutlined /> : <PlusOutlined />}
    </span>
  );

  return (
    <div style={{ margin: '40px 0', ...style }}>
      {/* FAQ Schema for Rich Snippets */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <Title level={2} style={{ 
        textAlign: 'center', 
        marginBottom: '40px',
        color: '#2c3e50',
        fontSize: '32px',
        fontWeight: '700'
      }}>
        {title}
      </Title>

      <div style={{ maxWidth: '800px', margin: '0 auto' }}>
        <Collapse
          activeKey={collapsible ? activeKeys : faqs.map((_, index) => index.toString())}
          onChange={collapsible ? handleChange : undefined}
          expandIcon={collapsible ? customExpandIcon : () => null}
          ghost={!collapsible}
          size="large"
        >
          {faqs.map((faq, index) => (
            <Panel
              header={
                <span style={{ 
                  fontWeight: '600',
                  fontSize: '16px',
                  color: '#2c3e50',
                  lineHeight: '1.5'
                }}>
                  {faq.question}
                </span>
              }
              key={index.toString()}
              style={{
                marginBottom: '16px',
                backgroundColor: collapsible ? '#fff' : 'transparent',
                border: collapsible ? '1px solid #e8f4fd' : 'none',
                borderRadius: collapsible ? '12px' : '0'
              }}
            >
              <div style={{ 
                color: '#4a5568',
                fontSize: '15px',
                lineHeight: '1.7',
                padding: collapsible ? '0 16px 8px 16px' : '16px 0'
              }}>
                {typeof faq.answer === 'string' ? (
                  <p style={{ margin: 0 }}>{faq.answer}</p>
                ) : (
                  faq.answer
                )}
              </div>
            </Panel>
          ))}
        </Collapse>
      </div>
    </div>
  );
};

// Common FAQ data for different sections
export const homepageFAQs = [
  {
    question: "What makes Lumi Bears different from regular stuffed animals?",
    answer: "Lumi Bears combine traditional comfort with innovative wellness technology. They feature voice interaction, emotional support capabilities, and parent oversight through a secure dashboard. Unlike regular stuffed animals, Lumi Bears actively help children develop emotional regulation skills and provide responsive support during challenging moments."
  },
  {
    question: "Are Lumi Bears safe for children?",
    answer: "Yes, Lumi Bears are designed with child safety as the top priority. All materials are child-safe and tested to exceed safety standards. The AI technology includes robust privacy protections, parental controls, and content filtering. All interactions are monitored and can be reviewed by parents through the secure dashboard."
  },
  {
    question: "What age range are Lumi Bears suitable for?",
    answer: "Lumi Bears are designed for children ages 3-12. The emotional support features and interaction complexity adapt to different developmental stages, ensuring age-appropriate experiences. Younger children benefit from simple comfort and routine support, while older children can engage in more sophisticated emotional regulation activities."
  },
  {
    question: "How do I know if my child needs an emotional support companion?",
    answer: "Children who experience big emotions, separation anxiety, bedtime challenges, social difficulties, or transitions may benefit from emotional support companions. Signs include difficulty self-soothing, frequent meltdowns, resistance to bedtime routines, or challenges in new social situations. Lumi Bears can provide additional support alongside your parenting."
  },
  {
    question: "Can parents monitor interactions with Lumi Bears?",
    answer: "Absolutely. Lumi Bears includes complete parental oversight through a secure dashboard. Parents can review all interactions, set interaction guidelines, and customize their child's experience. The technology is designed to enhance family connection rather than replace it, providing transparency and peace of mind."
  }
];

export const productFAQs = [
  {
    question: "How long does the battery last?",
    answer: "Lumi Bears feature long-lasting batteries designed for daily use. Typical usage provides 8-12 hours of active interaction time, with standby mode extending battery life significantly. The companion includes a gentle low-battery reminder and easy USB charging."
  },
  {
    question: "What happens if my child becomes too attached?",
    answer: "Healthy attachment to comfort objects is normal and beneficial for emotional development. Lumi Bears are designed to gradually build your child's independence and emotional skills. The parent dashboard includes guidance on promoting healthy boundaries while maintaining the emotional support benefits."
  },
  {
    question: "Is internet connection required?",
    answer: "Lumi Bears include both online and offline capabilities. Core comfort features and basic interaction work without internet connection. Enhanced features like learning updates and parent dashboard access require Wi-Fi connectivity. This ensures your child maintains access to emotional support even without internet."
  }
];

export const blogFAQs = [
  {
    question: "How do emotional support stuffed animals help with anxiety?",
    answer: "Emotional support stuffed animals provide consistent comfort during anxious moments, help children practice calming techniques, and offer a safe way to express worries. They serve as transitional objects that build emotional regulation skills and provide security during challenging situations."
  },
  {
    question: "Are AI interactive toys appropriate for young children?",
    answer: "When designed with child development expertise, AI interactive toys can provide valuable emotional support and learning opportunities. The key is choosing toys with age-appropriate features, robust safety measures, and parental oversight capabilities like those found in Lumi Bears."
  },
  {
    question: "How can stuffed animals help with bedtime routines?",
    answer: "Comfort companions help establish consistent bedtime routines, provide security during the vulnerable transition to sleep, offer opportunities to process daily experiences, and teach calming techniques like deep breathing. They create positive sleep associations that can last into adulthood."
  }
];

export default FAQSection;